import { TradingCheckbox, TradingLoadingButton, TradingTextField } from '@trading-ui/core';
import TradingSelect from '@trading-ui/select';
import { useTranslation } from 'react-i18next';

import type { CreateCompanyUserPayload, Plant } from '@/interfaces';

import { FormUtils } from '../../utils/form.utils';

import * as styles from './Form.css';

type Props = {
  form: CreateCompanyUserPayload;
  isSubmitting: boolean;
  errors: Partial<Record<keyof CreateCompanyUserPayload, string>>;
  updateField: (field: keyof CreateCompanyUserPayload, value: string) => void;
  updateRole: (role: 'MANAGER' | 'USER') => void;
  updatePlants: (plants: { id: number; value: string; label: string }[]) => void;
  handleSubmit: () => void;
  companyPlants: Plant[];
  type: 'create' | 'update';
};

export default function CompanyUserForm({
  form,
  isSubmitting,
  errors,
  companyPlants,
  updateField,
  updateRole,
  updatePlants,
  handleSubmit,
  type,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className={styles.formContainer}>
      <TradingTextField
        label={t('settings.companyDetailsTab.companyUsers.modal.nameLabel')}
        value={form.name}
        onChange={(e) => updateField('name', e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        disabled={type === 'update'}
      />
      <TradingTextField
        label={t('settings.companyDetailsTab.companyUsers.modal.emailLabel')}
        value={form.email}
        onChange={(e) => updateField('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        disabled={type === 'update'}
      />
      <div>
        <TradingCheckbox
          label={t('settings.companyDetailsTab.companyUsers.modal.managerLabel')}
          checked={form.role === 'MANAGER'}
          onChange={() => updateRole(form.role === 'MANAGER' ? 'USER' : 'MANAGER')}
        />
        <TradingCheckbox
          label={t('settings.companyDetailsTab.companyUsers.modal.userLabel')}
          checked={form.role === 'USER'}
          onChange={() => updateRole(form.role === 'USER' ? 'MANAGER' : 'USER')}
        />
      </div>
      <TradingSelect
        label={t('settings.companyDetailsTab.companyUsers.modal.associatedCupsLabel')}
        options={FormUtils.getPlantsOptions(companyPlants)}
        value={form.plants}
        onChange={(value) => updatePlants(value)}
        multiple
        style={{ width: '400px' }}
      />
      <TradingLoadingButton
        label={
          type === 'create'
            ? t('settings.companyDetailsTab.companyUsers.modal.createUsersButton')
            : t('settings.companyDetailsTab.companyUsers.modal.updateUsersButton')
        }
        handleClick={handleSubmit}
        className={styles.button}
        loading={isSubmitting}
      />
    </div>
  );
}
